import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Bot,
  BrainCircuit,
  Building2,
  GitBranch,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  MessageSquarePlus,
  Network,
  Rss,
  ScrollText,
  Send,
  Settings,
  Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, hint: "G D" },
  { to: "/knowledge", label: "Knowledge", icon: Network, hint: "G K" },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/sources", label: "Sources", icon: Rss },
  { to: "/editorial", label: "Editorial", icon: Lightbulb },
  { to: "/daily-briefs", label: "Daily Briefs", icon: Send },
  { to: "/memory", label: "Memory", icon: BrainCircuit },
  { to: "/agents", label: "Agents", icon: Bot },
  { to: "/workflows", label: "Workflows", icon: GitBranch },
  { to: "/logs", label: "Logs", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings, hint: "G S" },
];

export default function CommandPalette({
  open,
  onOpenChange,
  onToggleChat,
  onLogout,
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  const actions = useMemo(
    () => [
      {
        label: "New chat",
        icon: MessageSquarePlus,
        hint: "⌘J",
        run: () => {
          onOpenChange(false);
          onToggleChat?.(true);
        },
      },
      {
        label: "Sign out",
        icon: LogOut,
        run: () => {
          onOpenChange(false);
          onLogout?.();
        },
      },
    ],
    [onOpenChange, onToggleChat, onLogout],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 overflow-hidden border-hm-border-strong bg-hm-bg-2 shadow-hm-glow max-w-[640px]"
        data-testid="command-palette"
      >
        <VisuallyHidden asChild>
          <DialogTitle>Command palette</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>
            Search commands or navigate pages. Use arrow keys and Enter to select.
          </DialogDescription>
        </VisuallyHidden>
        <Command className="bg-transparent">
          <div className="flex items-center gap-2 border-b border-hm-border px-4 py-3">
            <Sparkles className="h-4 w-4 text-hm-accent" />
            <CommandInput
              autoFocus
              value={q}
              onValueChange={setQ}
              placeholder="Search commands or type to navigate…"
              className="bg-transparent border-0 focus-visible:ring-0 h-8 px-0 text-hm-fg"
              data-testid="command-palette-input"
            />
            <kbd className="mono text-[10px] px-1.5 py-0.5 rounded border border-hm-border bg-hm-bg-1 text-hm-fg-subtle">
              ESC
            </kbd>
          </div>
          <CommandList className="max-h-[420px] px-2 py-2">
            <CommandEmpty className="py-8 text-center mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No matches
            </CommandEmpty>
            <CommandGroup heading="Navigate">
              {NAV.map((item) => (
                <CommandItem
                  key={item.to}
                  value={`nav ${item.label}`}
                  onSelect={() => go(item.to)}
                  className="text-hm-fg-muted data-[selected=true]:bg-hm-bg-1 data-[selected=true]:text-hm-fg rounded-md"
                  data-testid={`cmd-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className="h-4 w-4 text-hm-fg-subtle" />
                  <span>{item.label}</span>
                  {item.hint && <CommandShortcut className="mono text-hm-fg-subtle">{item.hint}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Actions" className="mt-2">
              {actions.map((a) => (
                <CommandItem
                  key={a.label}
                  value={`action ${a.label}`}
                  onSelect={a.run}
                  className="text-hm-fg-muted data-[selected=true]:bg-hm-bg-1 data-[selected=true]:text-hm-fg rounded-md"
                >
                  <a.icon className="h-4 w-4 text-hm-fg-subtle" />
                  <span>{a.label}</span>
                  {a.hint && <CommandShortcut className="mono text-hm-fg-subtle">{a.hint}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
